import React, { useState, useEffect, useRef } from "react";
import { XIcon, BellIcon, CheckIcon, RefreshCwIcon } from "lucide-react";
import { Button } from "../ui/button";
import { NotificationItem } from "./NotificationItem";
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  updateNotificationActionStatus,
  acceptFriendRequest,
  rejectFriendRequest,
  Notification,
} from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../hooks/useNotifications";
import toast from "react-hot-toast";

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
  onUnreadCountChange,
}) => {
  const { user } = useAuth();
  const { refreshNotifications } = useNotifications();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Load notifications
  const loadNotifications = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const result = await getNotifications(user.id, 50); // Load more notifications for panel

      if (result.success && result.data) {
        setNotifications(result.data);

        // Update unread count
        const unreadCount = result.data.filter(n => !n.is_read).length;
        if (onUnreadCountChange) {
          onUnreadCountChange(unreadCount);
        }
      } else {
        toast.error(result.error || "Gagal memuat notifikasi");
      }
    } catch (error) {
      console.error("Error loading notifications:", error);
      toast.error("Terjadi kesalahan saat memuat notifikasi");
    } finally {
      setLoading(false);
    }
  };

  // Load notifications when panel opens
  useEffect(() => {
    if (isOpen && user?.id) {
      loadNotifications();
    }
  }, [isOpen, user?.id]);

  // Close panel when clicking outside - with setTimeout to prevent immediate reopening
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      // Add a small delay before attaching the event listener
      // This prevents the same click that opens the panel from immediately closing it
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleMarkAsRead = async (notificationId: string) => {
    if (!user?.id) return;

    try {
      const result = await markNotificationAsRead(notificationId, user.id);

      if (result.success) {
        // Update local state
        setNotifications(prev =>
          prev.map(n => (n.id === notificationId ? { ...n, is_read: true } : n))
        );

        // Refresh shared notification count
        await refreshNotifications();

        // Update unread count callback
        const unreadCount = notifications.filter(
          n => !n.is_read && n.id !== notificationId
        ).length;
        if (onUnreadCountChange) {
          onUnreadCountChange(unreadCount);
        }
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;

    setMarkingAllAsRead(true);
    try {
      const result = await markAllNotificationsAsRead(user.id);

      if (result.success) {
        // Update local state
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));

        // Update unread count to 0
        if (onUnreadCountChange) {
          onUnreadCountChange(0);
        }

        toast.success("Semua notifikasi telah ditandai sebagai dibaca");
      } else {
        toast.error(
          result.error || "Gagal menandai semua notifikasi sebagai dibaca"
        );
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      toast.error(
        "Terjadi kesalahan saat menandai semua notifikasi sebagai dibaca"
      );
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  const handleAcceptFriendRequest = async (
    notificationId: string,
    friendRequestId: string
  ) => {
    if (!user?.id) return;

    try {
      // Get the notification to extract sender info
      const notification = notifications.find(n => n.id === notificationId);
      if (!notification || !notification.metadata?.sender_id) {
        toast.error("Informasi permintaan pertemanan tidak valid");
        return;
      }

      const result = await acceptFriendRequest(
        friendRequestId,
        user.id,
        notification.metadata.sender_id
      );

      if (result.success) {
        // Update notification action status
        await updateNotificationActionStatus(
          notificationId,
          user.id,
          "accepted"
        );

        // Update local state
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId
              ? { ...n, action_status: "accepted", is_read: true }
              : n
          )
        );

        toast.success("Permintaan pertemanan diterima");
      } else {
        toast.error(result.error || "Gagal menerima permintaan pertemanan");
      }
    } catch (error) {
      console.error("Error accepting friend request:", error);
      toast.error("Terjadi kesalahan saat menerima permintaan pertemanan");
    }
  };

  const handleRejectFriendRequest = async (
    notificationId: string,
    friendRequestId: string
  ) => {
    if (!user?.id) return;

    try {
      const result = await rejectFriendRequest(friendRequestId);

      if (result.success) {
        // Update notification action status
        await updateNotificationActionStatus(
          notificationId,
          user.id,
          "rejected"
        );

        // Update local state
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId
              ? { ...n, action_status: "rejected", is_read: true }
              : n
          )
        );

        toast.success("Permintaan pertemanan ditolak");
      } else {
        toast.error(result.error || "Gagal menolak permintaan pertemanan");
      }
    } catch (error) {
      console.error("Error rejecting friend request:", error);
      toast.error("Terjadi kesalahan saat menolak permintaan pertemanan");
    }
  };

  const handleUpdateActionStatus = async (
    notificationId: string,
    status: string
  ) => {
    if (!user?.id) return;

    try {
      await updateNotificationActionStatus(notificationId, user.id, status);

      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, action_status: status, is_read: true }
            : n
        )
      );
    } catch (error) {
      console.error("Error updating notification action status:", error);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 transform transition-all duration-200 ease-in-out opacity-100 scale-100 max-h-[80vh] flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] rounded-lg flex items-center justify-center">
            <BellIcon className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[#373e46]">
              Notifikasi
            </h2>
            {unreadCount > 0 && (
              <p className="text-xs text-gray-500">
                {unreadCount} belum dibaca
              </p>
            )}
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors duration-200"
        >
          <XIcon className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200">
        <Button
          onClick={loadNotifications}
          disabled={loading}
          variant="outline"
          size="sm"
          className="flex items-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-100 h-7 px-2 text-xs"
        >
          <RefreshCwIcon
            className={`w-3 h-3 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>

        {unreadCount > 0 && (
          <Button
            onClick={handleMarkAllAsRead}
            disabled={markingAllAsRead}
            size="sm"
            className="bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white h-7 px-2 text-xs transition-all duration-300"
          >
            {markingAllAsRead ? (
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
            ) : (
              <CheckIcon className="w-3 h-3 mr-1" />
            )}
            Tandai Semua
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          /* Loading State */
          <div className="space-y-3 p-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-start gap-3 animate-pulse">
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-3 bg-gray-200 rounded mb-2"></div>
                  <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length > 0 ? (
          /* Notifications */
          <div>
            {notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onAcceptFriendRequest={handleAcceptFriendRequest}
                onRejectFriendRequest={handleRejectFriendRequest}
                onMarkAsRead={handleMarkAsRead}
                onUpdateActionStatus={handleUpdateActionStatus}
              />
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-3">
              <BellIcon className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-sm font-semibold text-[#373e46] mb-1">
              Belum Ada Notifikasi
            </h3>
            <p className="text-gray-500 text-xs mb-3">
              Notifikasi akan muncul di sini ketika ada aktivitas baru
            </p>
            <Button
              onClick={loadNotifications}
              size="sm"
              className="bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white px-4 py-1 rounded-lg font-medium transition-all duration-300 hover:shadow-lg text-xs h-7"
            >
              Refresh
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
